import { Icon, Text, Tooltip } from '@wyz/ui';
import { uniq, uniqBy } from 'lodash-es';
import * as React from 'react';
import { Trans } from '~/common/context';
import { TranslationKeys } from '~/common/types';
import { DISCOUNT_TYPE_TRANSLATIONS } from '~/common/translators';
import { DistributionPricingPolicyHelpers } from '~/purchase/helpers';
import {
  IDistributionPricingPolicySeason,
  IDistributionPricingPolicySeasonCell,
  IDistributionPricingPolicyVariantAmount,
  IPricingConfigurationType,
} from '~/purchase/types';

const {
  NO_VALUE,
  bonusSign,
  summarizeRange,
  tooltipClass,
  variantDiameterLabel,
} = DistributionPricingPolicyHelpers;

/** The row and the column a bonus restricted by nothing sits in. */
const EVERY_DIAMETER_ROW = 'ALL_DIAMETERS';
const EVERY_VEHICLE_TYPE_COLUMN = 'ALL_VEHICLE_TYPES';

/**
 * One amount of a divergent cell, placed in the tooltip grid by its diameter and vehicle type. The
 * column is keyed apart from its label: the purchase amounts name their vehicle type the way the
 * pricing API spells it, while a bonus carries the catalog one.
 */
type VariantDetail = {
  diameter: number | string;
  column: string;
  columnLabel: React.ReactNode;
  value: React.ReactNode;
};

/** An amount and the unit of its discount type, which not every discount type has. */
function Amount({
  value,
  discountType,
}: {
  value: number | string;
  discountType: number | null;
}) {
  const labelKey =
    null === discountType
      ? undefined
      : DISCOUNT_TYPE_TRANSLATIONS[discountType];

  return (
    <>
      {value}
      {labelKey && (
        <>
          {' '}
          <Trans i18nKey={labelKey} />
        </>
      )}
    </>
  );
}

export function PurchaseAmount({
  cell,
  season,
  printMode,
}: {
  cell: IDistributionPricingPolicySeasonCell;
  season: IDistributionPricingPolicySeason;
  printMode: boolean;
}) {
  if (0 === cell.variants.length) {
    return (
      <span className='text-center'>
        {null === cell.purchaseValue || null === cell.discountType ? (
          NO_VALUE
        ) : (
          <Amount value={cell.purchaseValue} discountType={cell.discountType} />
        )}
      </span>
    );
  }

  const range = summarizeRange(
    cell.variants.map((variant) => variant.purchaseValue),
    cell.variants.map((variant) => variant.discountType),
  );

  return (
    <VariantTooltip
      season={season}
      details={cell.variants.map((variant) => ({
        diameter: variant.diameter,
        column: variant.vehicleType,
        columnLabel: variant.vehicleType,
        value: (
          <Amount
            value={variant.purchaseValue}
            discountType={variant.discountType}
          />
        ),
      }))}
      printMode={printMode}
    >
      <span className='text-center'>
        {null === range ? (
          NO_VALUE
        ) : (
          <Amount value={range.text} discountType={range.discountType} />
        )}
      </span>
    </VariantTooltip>
  );
}

export function TotalAmount({
  cell,
  season,
  extraBonusType,
  hqBonus,
  hqBonusType,
  printMode,
}: {
  cell: IDistributionPricingPolicySeasonCell;
  season: IDistributionPricingPolicySeason;
  extraBonusType: IPricingConfigurationType | null;
  hqBonus: number | null;
  hqBonusType: IPricingConfigurationType | null;
  printMode: boolean;
}) {
  const computeTotal = (purchaseValue: number, discountType: number) => {
    const total = DistributionPricingPolicyHelpers.computeTotal(
      purchaseValue,
      discountType,
      cell.extraBonus,
      extraBonusType,
      hqBonus,
      hqBonusType,
    );

    return null === total ? null : Number(total.toFixed(2));
  };

  if (0 === cell.variants.length) {
    if (null === cell.purchaseValue || null === cell.discountType) {
      return NO_VALUE;
    }

    const total = computeTotal(cell.purchaseValue, cell.discountType);

    return (
      <Text color='primary' weight='semi-bold'>
        {null === total ? (
          NO_VALUE
        ) : (
          <Amount value={total} discountType={cell.discountType} />
        )}
      </Text>
    );
  }

  const totals = cell.variants
    .map((variant) => ({
      variant,
      total: computeTotal(variant.purchaseValue, variant.discountType),
    }))
    .filter(
      (
        entry,
      ): entry is {
        variant: IDistributionPricingPolicyVariantAmount;
        total: number;
      } => null !== entry.total,
    );

  if (0 === totals.length) {
    return NO_VALUE;
  }

  const range = summarizeRange(
    totals.map((entry) => entry.total),
    totals.map((entry) => entry.variant.discountType),
  );

  return (
    <VariantTooltip
      season={season}
      details={totals.map((entry) => ({
        diameter: entry.variant.diameter,
        column: entry.variant.vehicleType,
        columnLabel: entry.variant.vehicleType,
        value: (
          <Amount
            value={entry.total}
            discountType={entry.variant.discountType}
          />
        ),
      }))}
      printMode={printMode}
    >
      <Text component='span' color='primary' weight='semi-bold'>
        {null === range ? (
          NO_VALUE
        ) : (
          <Amount value={range.text} discountType={range.discountType} />
        )}
      </Text>
    </VariantTooltip>
  );
}

/**
 * The bonuses behind a cell. A single bonus restricted by nothing shows its amount; as soon as
 * diameters or categories are targeted, the cell summarises the range they span and moves the rules
 * into the tooltip, the way the purchase amounts already do.
 */
export function ExtraBonusAmount({
  cell,
  season,
  printMode,
}: {
  cell: IDistributionPricingPolicySeasonCell;
  season: IDistributionPricingPolicySeason;
  printMode: boolean;
}) {
  const variants = cell.extraBonusVariants ?? [];

  if (0 === variants.length) {
    return (
      <Text color='primary' weight='semi-bold'>
        {cell.extraBonus ?? NO_VALUE}
      </Text>
    );
  }

  // A bonus carries its own type rather than a discount type, and the sign already shows it.
  const range = summarizeRange(
    variants.map((variant) => variant.value),
    [],
  );

  return (
    <VariantTooltip
      season={season}
      details={variants.map((variant) => ({
        diameter: variantDiameterLabel(variant) ?? EVERY_DIAMETER_ROW,
        column: variant.vehicleType?.key ?? EVERY_VEHICLE_TYPE_COLUMN,
        columnLabel: variant.vehicleType ? (
          <Trans
            i18nKey={variant.vehicleType.i18nKey as keyof TranslationKeys}
            // The catalog name is the label of last resort, when no loco key covers it.
            values={{ defaultValue: variant.vehicleType.name }}
          />
        ) : (
          <Trans i18nKey='app.distribution.pricing_policy.detail.advanced.every_vehicle_type' />
        ),
        value: `${variant.value} ${bonusSign(variant.bonusType)}`,
      }))}
      printMode={printMode}
    >
      <Text component='span' color='primary' weight='semi-bold'>
        {null === range ? NO_VALUE : range.text}
      </Text>
    </VariantTooltip>
  );
}

/**
 * The combinations behind a divergent cell read as a grid: one column per vehicle type, one row per
 * diameter. A combination the pricing policy does not cover leaves its cell empty.
 */
function VariantTooltip({
  details,
  season,
  printMode,
  children,
}: {
  details: VariantDetail[];
  season: IDistributionPricingPolicySeason;
  printMode: boolean;
  children?: React.ReactNode;
}) {
  const columns = uniqBy(details, (detail) => detail.column);
  const diameters = uniq(details.map((detail) => detail.diameter)).sort(
    (left, right) => (left > right ? 1 : -1),
  );
  const valueOf = (diameter: number | string, column: string) =>
    details.find(
      (detail) => detail.diameter === diameter && detail.column === column,
    )?.value;

  return printMode ? (
    children
  ) : (
    <Tooltip
      className={`extra-bonus-tooltip${tooltipClass(season)}`}
      label={
        <table className='extra-bonus-tooltip__variants'>
          <thead>
            <tr>
              <th />
              {columns.map((column) => (
                <th key={column.column}>{column.columnLabel}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {diameters.map((diameter) => (
              <tr key={diameter}>
                <th>
                  {EVERY_DIAMETER_ROW === diameter ? (
                    <Trans i18nKey='app.quote.pricing_policy.form.step1.all_diameters' />
                  ) : (
                    <Trans
                      i18nKey='app.distribution.pricing_policy.detail.diameter'
                      values={{ diameter: String(diameter) }}
                    />
                  )}
                </th>
                {columns.map((column) => (
                  <td key={column.column}>
                    {valueOf(diameter, column.column) ?? NO_VALUE}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <span className='extra-bonus-table__divergent d-flex align-items-center justify-content-center'>
        {children}
        <span className='extra-bonus-table__magnifier'>
          <Icon icon='search' size='md' style={{ transform: 'scale(0.58)' }} />
        </span>
      </span>
    </Tooltip>
  );
}
